import type { ReactNode } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { ArrowBackRounded } from '@mui/icons-material';

// Single source of truth for the CAP journey pages' content width, so every page
// (journey + PLOs, matrix, compliance, proposal) fills the container identically.
export const CAP_CONTENT_MAX_WIDTH = 1280;

// Shared sticky header for every CAP journey page (journey + PLOs, matrix,
// compliance, proposal) so they read as one system: back breadcrumb, title,
// secondary line, and an optional right-aligned action slot.

interface CapPageHeaderProps {
  backLabel: string;
  onBack: () => void;
  title: string;
  subtitle?: string;
  right?: ReactNode;
}

export function CapPageHeader({
  backLabel,
  onBack,
  title,
  subtitle,
  right,
}: CapPageHeaderProps): JSX.Element {
  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 10,
        bgcolor: 'background.default',
        borderBottom: '1px solid',
        borderColor: 'divider',
        px: 3,
        py: 2,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} minWidth={0}>
          <Button
            startIcon={<ArrowBackRounded />}
            onClick={onBack}
            sx={{ textTransform: 'none', color: 'text.secondary', flexShrink: 0 }}
          >
            {backLabel}
          </Button>
          <Typography color="text.disabled">/</Typography>
          <Box minWidth={0}>
            <Typography variant="h6" fontWeight={700} lineHeight={1.2} noWrap>
              {title}
            </Typography>
            {subtitle && (
              <Typography variant="body2" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
        </Stack>
        {right}
      </Stack>
    </Box>
  );
}
